import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';

import { useCreateWorkPackage } from './useWorkPackages';
import type { I18nText, WorkPackageDto } from './types';

const schema = z.object({
  package_code: z
    .string()
    .min(2)
    .regex(/^[a-z][a-z0-9_]*$/, 'use lowercase letters, numbers, and underscores'),
  name_ja: z.string().min(1),
  name_zh: z.string().optional(),
  name_en: z.string().optional(),
  description_ja: z.string().optional(),
  description_zh: z.string().optional(),
  description_en: z.string().optional(),
  party_type: z.enum(['supplier', 'processor', 'platform']),
  affects_quotation: z.boolean(),
  affects_report: z.boolean(),
  affects_settlement: z.boolean(),
});

type WorkPackageCreateValues = z.infer<typeof schema>;

const DEFAULTS: WorkPackageCreateValues = {
  package_code: '',
  name_ja: '',
  name_zh: '',
  name_en: '',
  description_ja: '',
  description_zh: '',
  description_en: '',
  party_type: 'platform',
  affects_quotation: false,
  affects_report: false,
  affects_settlement: false,
};

const buildI18n = (ja: string, zh?: string, en?: string): I18nText => {
  const blob: I18nText = { ja: ja.trim() };
  if (zh?.trim()) blob['zh-TW'] = zh.trim();
  if (en?.trim()) blob.en = en.trim();
  return blob;
};

interface WorkPackageCreateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated?: (pkg: WorkPackageDto) => void;
}

export const WorkPackageCreateDialog = ({
  open,
  onOpenChange,
  onCreated,
}: WorkPackageCreateDialogProps) => {
  const { t } = useTranslation('settings');
  const create = useCreateWorkPackage();

  const form = useForm<WorkPackageCreateValues>({
    resolver: zodResolver(schema),
    defaultValues: DEFAULTS,
  });

  useEffect(() => {
    if (!open) form.reset(DEFAULTS);
  }, [form, open]);

  const submit = form.handleSubmit(async (values) => {
    const description = buildI18n(
      values.description_ja ?? '',
      values.description_zh,
      values.description_en,
    );
    const hasDescription = !!(description.ja || description['zh-TW'] || description.en);
    const pkg = await create.mutateAsync({
      package_code: values.package_code.trim(),
      name_i18n: buildI18n(values.name_ja, values.name_zh, values.name_en),
      description_i18n: hasDescription ? description : undefined,
      party_type: values.party_type,
      affects_quotation: values.affects_quotation,
      affects_report: values.affects_report,
      affects_settlement: values.affects_settlement,
      permission_codes: [],
    });
    onCreated?.(pkg);
    onOpenChange(false);
  });

  const affectsFields = [
    { name: 'affects_quotation', label: t('efs.settings.work_packages.affects.quotation') },
    { name: 'affects_report', label: t('efs.settings.work_packages.affects.report') },
    { name: 'affects_settlement', label: t('efs.settings.work_packages.affects.settlement') },
  ] as const;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="overflow-y-auto">
        <SheetHeader>
          <SheetTitle>{t('efs.settings.work_packages.actions.create')}</SheetTitle>
          <SheetDescription>{t('efs.settings.work_packages.create_hint')}</SheetDescription>
        </SheetHeader>

        <Form {...form}>
          <form onSubmit={submit} className="mt-6 space-y-4" noValidate>
            <FormField
              control={form.control}
              name="package_code"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('efs.settings.work_packages.fields.package_code')}</FormLabel>
                  <FormControl>
                    <Input placeholder="supplier_quotation" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="name_ja"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('efs.settings.work_packages.fields.name_ja')}</FormLabel>
                  <FormControl>
                    <Input autoFocus {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="name_zh"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('efs.settings.work_packages.fields.name_zh')}</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="name_en"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('efs.settings.work_packages.fields.name_en')}</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="party_type"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('efs.settings.work_packages.fields.party_type')}</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {(['platform', 'processor', 'supplier'] as const).map((party) => (
                        <SelectItem key={party} value={party}>
                          {t(`efs.settings.companies.types.${party}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="description_ja"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('efs.settings.work_packages.fields.description_ja')}</FormLabel>
                  <FormControl>
                    <Textarea rows={2} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="description_zh"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('efs.settings.work_packages.fields.description_zh')}</FormLabel>
                  <FormControl>
                    <Textarea rows={2} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="description_en"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('efs.settings.work_packages.fields.description_en')}</FormLabel>
                  <FormControl>
                    <Textarea rows={2} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div>
              <p className="mb-2 text-sm font-medium text-efs-text-primary">
                {t('efs.settings.work_packages.fields.affects')}
              </p>
              <div className="space-y-2">
                {affectsFields.map((entry) => (
                  <FormField
                    key={entry.name}
                    control={form.control}
                    name={entry.name}
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-center gap-2 space-y-0">
                        <FormControl>
                          <input
                            type="checkbox"
                            className="h-4 w-4"
                            checked={field.value}
                            onChange={(event) => field.onChange(event.target.checked)}
                          />
                        </FormControl>
                        <FormLabel>{entry.label}</FormLabel>
                      </FormItem>
                    )}
                  />
                ))}
              </div>
            </div>
            {create.error ? (
              <p className="text-sm text-destructive">{create.error.message}</p>
            ) : null}
            <SheetFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                {t('efs.common.actions.cancel', { ns: 'common' })}
              </Button>
              <Button type="submit" disabled={create.isPending}>
                {create.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                {t('efs.settings.work_packages.actions.create')}
              </Button>
            </SheetFooter>
          </form>
        </Form>
      </SheetContent>
    </Sheet>
  );
};
